import { Link } from '@tanstack/react-router';
import { formatInTimeZone } from 'date-fns-tz';

import { OUTREACH_ANALYTICS_TIMEZONE } from '@apps/web/components/admin/outreach_observability/constants';
import { Badge } from '@apps/web/components/ui/badge';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@apps/web/components/ui/card';
import { type ToolLoopRun } from '@libraries/typescript/api_client';

type LoopRunsPanelProps = {
    readonly loopRuns: Array<ToolLoopRun>;
};

function formatCallTime(iso: string): string {
    return formatInTimeZone(
        new Date(iso),
        OUTREACH_ANALYTICS_TIMEZONE,
        'h:mm a',
    );
}

function LoopRunRow({ run }: { readonly run: ToolLoopRun }) {
    return (
        <div className="flex items-center justify-between gap-3 border-t px-3 py-2 text-sm first:border-t-0">
            <div className="flex min-w-0 flex-col">
                <span className="flex items-center gap-2">
                    <span className="min-w-0 truncate font-medium">
                        {run.tool_name}
                    </span>
                    <Badge variant="destructive">×{run.repeat_count}</Badge>
                </span>
                <span className="text-muted-foreground min-w-0 truncate text-xs">
                    {run.org_name}
                </span>
            </div>
            <div className="flex shrink-0 items-center gap-3">
                <span className="text-muted-foreground text-xs tabular-nums">
                    {formatCallTime(run.first_call_at)} →{' '}
                    {formatCallTime(run.last_call_at)}
                </span>
                {run.agent_conversation_id ? (
                    <Link
                        to="/admin/agent-traces/$conversationId"
                        params={{ conversationId: run.agent_conversation_id }}
                        className="text-primary text-xs hover:underline"
                    >
                        Trace
                    </Link>
                ) : null}
            </div>
        </div>
    );
}

export function LoopRunsPanel({ loopRuns }: LoopRunsPanelProps) {
    return (
        <Card>
            <CardHeader>
                <CardTitle>Tool loops ({loopRuns.length})</CardTitle>
                <CardDescription>
                    Runs where one tool repeated with identical arguments three
                    or more times — the highest-signal agent alert.
                </CardDescription>
            </CardHeader>
            <CardContent>
                {loopRuns.length === 0 ? (
                    <p className="text-muted-foreground py-6 text-center text-sm">
                        No tool loops detected.
                    </p>
                ) : (
                    <div className="rounded-md border">
                        {loopRuns.map((run) => (
                            <LoopRunRow
                                // A single run can emit multiple loop rows (one
                                // per looping tool / arg-set), so run_id alone
                                // is not unique — key by tool and first call too.
                                key={`${run.run_id}:${run.tool_name}:${run.first_call_at}`}
                                run={run}
                            />
                        ))}
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
